import { supabase } from '../lib/supabase';
import type {
  PageType,
  ReferrerSource,
  PageAnalytics,
  FormAnalytics,
  TrafficSourceData,
  OverviewStats,
  TrackPagePayload,
  TrackFormPayload,
} from '../types/analytics';
import { logger } from '../lib/logger';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

function getDaysAgo(days: number = 7): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

function getSevenDaysAgo(): string {
  return getDaysAgo(7);
}

function normalizeReferrer(referrer: string | null): ReferrerSource {
  if (!referrer) return 'direct';
  const lower = referrer.toLowerCase();
  if (lower.includes('instagram')) return 'instagram';
  if (lower.includes('facebook') || lower.includes('fb.')) return 'facebook';
  if (lower.includes('tiktok') || lower.includes('t.co')) return 'tiktok';
  return 'other';
}

export async function trackPageEvent(payload: TrackPagePayload): Promise<void> {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/track-page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    logger.error('Failed to track page event:', error);
  }
}

export async function trackFormEvent(payload: TrackFormPayload): Promise<void> {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/track-form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    logger.error('Failed to track form event:', error);
  }
}

export async function getPageAnalytics(
  userId: string,
  pageType: PageType,
  days: number = 7
): Promise<PageAnalytics> {
  const startDate = getDaysAgo(days);

  const { data: pageViews, error: viewsError } = await supabase
    .from('page_events')
    .select('id, referrer, max_scroll_depth')
    .eq('user_id', userId)
    .eq('page_type', pageType)
    .eq('event_type', 'page_view')
    .gte('created_at', startDate);

  if (viewsError) throw viewsError;

  const { data: sitePage } = await supabase
    .from('site_pages')
    .select('slug')
    .eq('user_id', userId)
    .eq('type', pageType)
    .maybeSingle();

  let submissions = 0;
  if (sitePage?.slug) {
    const { count, error: submissionsError } = await supabase
      .from('form_submissions')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('origin_page_type', pageType)
      .gte('submitted_at', startDate);

    if (!submissionsError && count) {
      submissions = count;
    }
  }

  const viewCount = pageViews?.length || 0;
  const conversionRate = viewCount > 0 ? (submissions / viewCount) * 100 : 0;

  const scrollDistribution = { depth25: 0, depth50: 0, depth75: 0, depth100: 0 };
  pageViews?.forEach((view) => {
    const maxDepth = view.max_scroll_depth;
    if (maxDepth !== null && maxDepth !== undefined) {
      if (maxDepth >= 25) scrollDistribution.depth25++;
      if (maxDepth >= 50) scrollDistribution.depth50++;
      if (maxDepth >= 75) scrollDistribution.depth75++;
      if (maxDepth >= 100) scrollDistribution.depth100++;
    }
  });

  const referrerCounts: Record<ReferrerSource, number> = {
    instagram: 0,
    facebook: 0,
    tiktok: 0,
    direct: 0,
    other: 0,
  };
  pageViews?.forEach((view) => {
    const source = normalizeReferrer(view.referrer);
    referrerCounts[source]++;
  });

  const topReferrer = (Object.entries(referrerCounts) as [ReferrerSource, number][])
    .sort(([, a], [, b]) => b - a)[0]?.[0] || 'direct';

  return {
    pageType,
    pageViews: viewCount,
    submissions,
    conversionRate: Math.round(conversionRate * 10) / 10,
    scrollDistribution,
    topReferrer,
  };
}

export async function getFormAnalytics(
  userId: string,
  formId: string,
  formName: string,
  days: number = 7
): Promise<FormAnalytics> {
  const startDate = getDaysAgo(days);

  const { count: viewCount, error: viewsError } = await supabase
    .from('form_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('form_id', formId)
    .eq('event_type', 'view')
    .gte('created_at', startDate);

  if (viewsError) throw viewsError;

  const { count: startCount, error: startsError } = await supabase
    .from('form_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('form_id', formId)
    .eq('event_type', 'start')
    .gte('created_at', startDate);

  if (startsError) throw startsError;

  const { count: submissionCount, error: submissionsError } = await supabase
    .from('form_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('form_id', formId)
    .gte('submitted_at', startDate);

  if (submissionsError) throw submissionsError;

  const views = viewCount || 0;
  const starts = startCount || 0;
  const submissions = submissionCount || 0;

  const viewToSubmit = views > 0 ? (submissions / views) * 100 : 0;
  const startToSubmit = starts > 0 ? (submissions / starts) * 100 : 0;

  return {
    formId,
    formName,
    views,
    starts,
    submissions,
    viewToSubmitConversion: Math.round(viewToSubmit * 10) / 10,
    startToSubmitConversion: Math.round(startToSubmit * 10) / 10,
  };
}

export async function getAllFormsAnalytics(userId: string, days: number = 7): Promise<FormAnalytics[]> {
  const { data: forms, error: formsError } = await supabase
    .from('forms')
    .select('id, name')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (formsError) throw formsError;
  if (!forms || forms.length === 0) return [];

  const analytics: FormAnalytics[] = [];
  for (const form of forms) {
    const formAnalytics = await getFormAnalytics(userId, form.id, form.name, days);
    analytics.push(formAnalytics);
  }

  return analytics;
}

export async function getTrafficSources(userId: string, days: number = 7): Promise<TrafficSourceData[]> {
  const startDate = getDaysAgo(days);

  const { data: pageViews, error } = await supabase
    .from('page_events')
    .select('referrer')
    .eq('user_id', userId)
    .eq('event_type', 'page_view')
    .gte('created_at', startDate);

  if (error) throw error;

  const counts: Record<ReferrerSource, number> = {
    instagram: 0,
    facebook: 0,
    tiktok: 0,
    direct: 0,
    other: 0,
  };

  pageViews?.forEach((view) => {
    const source = normalizeReferrer(view.referrer);
    counts[source]++;
  });

  return (Object.entries(counts) as [ReferrerSource, number][])
    .map(([source, pageViews]) => ({ source, pageViews }))
    .sort((a, b) => b.pageViews - a.pageViews);
}

export async function getOverviewStats(userId: string, days: number = 7): Promise<OverviewStats> {
  const startDate = getDaysAgo(days);

  const { count: pageViewCount, error: pageViewsError } = await supabase
    .from('page_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('event_type', 'page_view')
    .gte('created_at', startDate);

  if (pageViewsError) throw pageViewsError;

  const { count: submissionCount, error: submissionsError } = await supabase
    .from('form_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('submitted_at', startDate);

  if (submissionsError) throw submissionsError;

  const { count: leadCount, error: leadsError } = await supabase
    .from('leads')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', startDate);

  if (leadsError) throw leadsError;

  return {
    totalPageViews: pageViewCount || 0,
    totalFormSubmissions: submissionCount || 0,
    totalLeads: leadCount || 0,
  };
}

export async function getDashboardFormStats(
  userId: string
): Promise<{ name: string; views: number; submissions: number; conversion: number }[]> {
  const sevenDaysAgo = getSevenDaysAgo();

  const { data: forms, error: formsError } = await supabase
    .from('forms')
    .select('id, name')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (formsError) throw formsError;
  if (!forms || forms.length === 0) return [];

  const results: { name: string; views: number; submissions: number; conversion: number }[] = [];

  for (const form of forms) {
    const { count: viewCount } = await supabase
      .from('form_events')
      .select('id', { count: 'exact', head: true })
      .eq('form_id', form.id)
      .eq('event_type', 'view')
      .gte('created_at', sevenDaysAgo);

    const { count: submissionCount } = await supabase
      .from('form_submissions')
      .select('id', { count: 'exact', head: true })
      .eq('form_id', form.id)
      .gte('submitted_at', sevenDaysAgo);

    const views = viewCount || 0;
    const submissions = submissionCount || 0;
    const conversion = views > 0 ? Math.round((submissions / views) * 1000) / 10 : 0;

    results.push({ name: form.name, views, submissions, conversion });
  }

  return results
    .sort((a, b) => b.submissions - a.submissions)
    .slice(0, 3);
}

export async function getPageStatsForWebsite(
  userId: string,
  pageType: PageType
): Promise<{ views: number; submissions: number; conversion: number }> {
  const sevenDaysAgo = getSevenDaysAgo();

  const { count: viewCount, error: viewsError } = await supabase
    .from('page_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('page_type', pageType)
    .eq('event_type', 'page_view')
    .gte('created_at', sevenDaysAgo);

  if (viewsError) throw viewsError;

  const { count: submissionCount, error: submissionsError } = await supabase
    .from('form_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('origin_page_type', pageType)
    .gte('submitted_at', sevenDaysAgo);

  if (submissionsError) throw submissionsError;

  const views = viewCount || 0;
  const submissions = submissionCount || 0;
  const conversion = views > 0 ? Math.round((submissions / views) * 1000) / 10 : 0;

  return { views, submissions, conversion };
}

export async function getLeadsThisWeek(userId: string): Promise<number> {
  const sevenDaysAgo = getSevenDaysAgo();

  const { count, error } = await supabase
    .from('leads')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', sevenDaysAgo);

  if (error) throw error;
  return count || 0;
}

export async function getFormSubmissionsThisWeek(userId: string): Promise<number> {
  const sevenDaysAgo = getSevenDaysAgo();

  const { count, error } = await supabase
    .from('form_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('submitted_at', sevenDaysAgo);

  if (error) throw error;
  return count || 0;
}

export async function getPageViewsThisWeek(userId: string): Promise<number> {
  const sevenDaysAgo = getSevenDaysAgo();

  const { count, error } = await supabase
    .from('page_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('event_type', 'page_view')
    .gte('created_at', sevenDaysAgo);

  if (error) throw error;
  return count || 0;
}
